import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { GameMode, Complexity, RoomMetadata, PlayerProfile, HandicapLevel, AIDifficulty } from "../../shared/types";
import { formatTimeLimit } from "../../shared/utils";
import { api } from "../lib/network";
import RoomLobby from "../components/lobby/RoomLobby";

function FeatureCard({
  icon,
  title,
  desc,
  tags,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  tags: string[];
}) {
  return (
    <div className="group relative bg-board-cell rounded-xl p-6 border border-board-border hover:border-vsw-400/40 transition-all hover:-translate-y-1 card-glow">
      <div className="mb-4 text-vsw-500">{icon}</div>
      <h3 className="font-display text-lg font-bold text-vsw-200 uppercase tracking-wider mb-2">
        {title}
      </h3>
      <p className="text-vsw-300 text-sm leading-relaxed mb-4">{desc}</p>
      <div className="flex flex-wrap gap-2">
        {tags.map((t) => (
          <span
            key={t}
            className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded bg-vsw-700/40 text-vsw-200 border border-vsw-700/60"
          >
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function StepNumber({ n }: { n: number }) {
  return (
    <div className="w-10 h-10 rounded-full bg-vsw-500 flex items-center justify-center font-display font-black text-white text-lg shrink-0">
      {n}
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [player, setPlayer] = useState<PlayerProfile | null>(null);
  const [rooms, setRooms] = useState<RoomMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");


  useEffect(() => {
    async function init() {
      try {
        const saved = localStorage.getItem("sw_player");
        let profile: PlayerProfile;
        if (saved) {
          profile = JSON.parse(saved);
        } else {
          profile = await api.createSession();
          localStorage.setItem("sw_player", JSON.stringify(profile));
        }
        setPlayer(profile);
        const list = await api.listRooms();
        setRooms(list);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to initialize");
      } finally {
        setLoading(false);
      }
    }
    init();
  }, []);

  const refreshRooms = useCallback(async () => {
    try {
      const list = await api.listRooms();
      setRooms(list);
    } catch {
      // ignore
    }
  }, []);

  const handleCreateRoom = async (settings: {
    mode: GameMode;
    complexity: Complexity;
    timeLimitMs: number;
    aiDifficulty: string | null;
    handicap: HandicapLevel;
  }) => {
    if (!player) return;
    setLoading(true);
    try {
      const room = await api.createRoom({
        playerId: player.id,
        mode: settings.mode,
        complexity: settings.complexity,
        timeLimitMs: settings.timeLimitMs,
        aiDifficulty: settings.aiDifficulty as any,
        handicap: settings.handicap,
      });
      if (settings.aiDifficulty) {
        await api.joinRoom(room.roomId, player.id);
        await api.startGame({
          roomId: room.roomId,
          mode: settings.mode,
          complexity: settings.complexity,
          timeLimitMs: settings.timeLimitMs,
          aiDifficulty: settings.aiDifficulty,
          handicap: settings.handicap,
        });
      }
      navigate(`/game/${room.roomId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create room");
      setLoading(false);
    }
  };

  const handleJoinRoom = async (roomId: string) => {
    if (!player) return;
    setLoading(true);
    try {
      const room = await api.joinRoom(roomId, player.id);
      if (room.status === "active") {
        await api.startGame({
          roomId: room.roomId,
          mode: room.mode,
          complexity: room.complexity,
          timeLimitMs: room.timeLimitMs,
          aiDifficulty: room.aiDifficulty || undefined,
          handicap: room.handicap,
        });
      }
      navigate(`/game/${room.roomId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to join room");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full gap-8">
        <div className="text-center">
          <img src="/VSWordFull.png" alt="VSWORD" className="h-28 sm:h-36 w-auto object-contain mx-auto" />
          <p className="font-display text-vsw-200 mt-3 text-base sm:text-lg tracking-wider uppercase">
            The Competitive Word Puzzle Race
          </p>
        </div>
        <div className="text-vsw-300">Loading...</div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      {/* HERO */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_#1a3a5c_0%,_#0a1424_70%)]" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoNzgsMTk1LDI0MiwwLjA1KSIvPjwvc3ZnPg==')] opacity-40" />
        <div className="relative max-w-5xl mx-auto px-4 pt-10 pb-12 sm:pt-16 sm:pb-20 text-center">
          <img
            src="/VSWordFull.png"
            alt="VSWORD"
            className="h-32 sm:h-48 w-auto object-contain mx-auto drop-shadow-[0_0_40px_rgba(242,116,5,0.4)]"
          />
          <p className="font-display text-vsw-200 mt-4 sm:mt-6 text-sm sm:text-xl tracking-[0.2em] uppercase">
            The Competitive Word Puzzle Race
          </p>
          <p className="text-vsw-300 mt-3 max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
             Race against friends or AI to solve grid puzzles at lightning speed.
             Place tiles, form words, and claim victory before the clock runs out.
          </p>
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
             <button
               onClick={() => {
                 const el = document.getElementById("play-lobby");
                 el?.scrollIntoView({ behavior: "smooth" });
               }}
               className="btn-gradient font-display font-bold uppercase tracking-wider text-white px-8 py-3 rounded-lg shadow-lg shadow-vsw-500/20 hover:shadow-vsw-500/40 transition-all hover:scale-105 text-sm"
             >
               Play Now
             </button>
             <button
               onClick={() => {
                 const el = document.getElementById("how-to-play");
                 el?.scrollIntoView({ behavior: "smooth" });
               }}
               className="px-8 py-3 rounded-lg border border-vsw-400/40 text-vsw-200 hover:bg-vsw-400/10 transition-all text-sm font-medium"
             >
               How to Play
             </button>
             <button
               onClick={() => {
                 if (!player) return;
                 const modes: GameMode[] = ["arcade", "grid"];
                 const complexities: Complexity[] = ["low", "medium", "high"];
                 const timeOptions = [180000, 210000, 240000, 270000, 300000];
const diffs: AIDifficulty[] = ["easy", "medium", "hard"];
                  handleCreateRoom({
                    mode: modes[Math.floor(Math.random() * modes.length)],
                    complexity: complexities[Math.floor(Math.random() * complexities.length)],
                    timeLimitMs: timeOptions[Math.floor(Math.random() * timeOptions.length)],
                    aiDifficulty: diffs[Math.floor(Math.random() * diffs.length)],
                    handicap: "none",
                  });
               }}
               className="px-8 py-3 rounded-lg bg-board-bg text-vsw-200 hover:bg-vsw-400/20 transition-all text-sm font-medium flex items-center gap-2"
               title="Start a random solo game against AI"
             >
               <span>🎲</span> Random Solo
             </button>
           </div>
          {player && (
            <p className="text-vsw-300 text-xs mt-4">
              Playing as <span className="text-vsw-200 font-medium">{player.username}</span>
            </p>
          )}
        </div>
      </section>

      {/* GAME MODES */}
      <section className="relative max-w-5xl mx-auto px-4 py-10 sm:py-16">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="font-display text-2xl sm:text-3xl font-black text-gradient-orange uppercase tracking-wider">
            Game Modes
          </h2>
          <p className="text-vsw-300 mt-2 text-sm">Two ways to race. One word at a time.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <FeatureCard
            icon={
              <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
            title="Grid"
            desc="A generated puzzle with clues and black squares. Fill in the white squares using your tile rack. Accuracy and speed both matter. Perfect for grid puzzle purists."
            tags={["Grid Clues", "Puzzle Grid", "Accuracy", "3-5 min"]}
          />
          <FeatureCard
            icon={
              <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672L13.684 16.6m-1.084-1.084l1.084 1.084m5.131-5.131l-1.084-1.084m-1.084 1.084l1.084 1.084m-9.168-5.263l1.084 1.084M7.84 14.4l-1.084-1.084m1.084 1.084l1.084 1.084m11.374-1.084l-1.084 1.084m1.084-1.084l-1.084-1.084M3 21l1.084-1.084M4.084 22.084L3 21m9.168-9.168L11.084 10.748m1.084 1.084l-1.084-1.084M3 21h18" />
              </svg>
            }
            title="Arcade"
            desc="Freeform speed-tile placement on an open grid. Draw tiles from your rack and place words anywhere. Build the highest-scoring board before time runs out. Great for creative word builders."
            tags={["Open Grid", "Tile Drawing", "High Score", "3-5 min"]}
          />
        </div>
      </section>

      {/* HOW TO PLAY */}
      <section id="how-to-play" className="relative bg-board-cell/50 border-y border-board-border">
        <div className="max-w-5xl mx-auto px-4 py-10 sm:py-16">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="font-display text-2xl sm:text-3xl font-black text-gradient-blue uppercase tracking-wider">
              How to Play
            </h2>
            <p className="text-vsw-300 mt-2 text-sm">Three steps to your first race.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
            <div className="flex flex-col items-center text-center">
              <StepNumber n={1} />
              <h3 className="font-display font-bold text-vsw-200 uppercase tracking-wider mt-4 mb-2 text-sm">
                Create or Join
              </h3>
              <p className="text-vsw-300 text-sm leading-relaxed">
                Pick your mode, difficulty, and time limit. Play solo against AI or challenge a friend.
              </p>
            </div>
            <div className="flex flex-col items-center text-center">
              <StepNumber n={2} />
              <h3 className="font-display font-bold text-vsw-200 uppercase tracking-wider mt-4 mb-2 text-sm">
                Race the Clock
              </h3>
              <p className="text-vsw-300 text-sm leading-relaxed">
                Drag tiles from your rack onto the board. Form words, intersect letters, and maximize your score.
              </p>
            </div>
            <div className="flex flex-col items-center text-center">
              <StepNumber n={3} />
              <h3 className="font-display font-bold text-vsw-200 uppercase tracking-wider mt-4 mb-2 text-sm">
                Submit & Win
              </h3>
              <p className="text-vsw-300 text-sm leading-relaxed">
                Hit Done when you're confident. The player with the highest valid score wins the race.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PLAY / LOBBY */}
      <section id="play-lobby" className="relative max-w-5xl mx-auto px-4 py-10 sm:py-16">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="font-display text-2xl sm:text-3xl font-black text-gradient-orange uppercase tracking-wider">
            Start Racing
          </h2>
          <p className="text-vsw-300 mt-2 text-sm">Configure your match and jump in.</p>
        </div>

        {error && (
          <div className="bg-red-500/20 text-red-300 px-4 py-2 rounded-lg text-sm max-w-xl mx-auto mb-6">
            {error}
          </div>
        )}

        <div className="w-full max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <RoomLobby onStartGame={handleCreateRoom} />
          </div>
          <div className="lg:col-span-2">
            <div className="bg-board-cell rounded-xl p-5 sm:p-6 border border-board-border h-full">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg font-bold text-vsw-400 uppercase tracking-wider">
                  Open Rooms
                </h2>
                <button
                  onClick={refreshRooms}
                  className="text-xs text-vsw-300 hover:text-vsw-200 flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
              </div>

              {rooms.length === 0 ? (
                <div className="text-vsw-300 text-sm italic text-center py-8">
                  No open rooms right now. Create one above!
                </div>
              ) : (
                <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                  {rooms.map((room) => (
                    <div
                      key={room.roomId}
                      className="flex items-center justify-between bg-board-bg rounded-lg p-3 border border-board-border hover:border-vsw-400/30 transition-colors"
                    >
                      <div className="text-sm">
                        <div className="text-vsw-200 font-medium">{room.p1Name}</div>
                        <div className="text-vsw-300 text-xs">
                          {room.mode === "arcade" ? "Arcade" : "Grid"} · {" "}
                          <span className="capitalize">{room.complexity}</span> ·{" "}
                          {formatTimeLimit(room.timeLimitMs)}
                        </div>
                      </div>
                      <button
                        onClick={() => handleJoinRoom(room.roomId)}
                        className="btn-gradient text-white px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider hover:scale-105 transition-transform"
                      >
                        Join
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* LEADERBOARD CTA */}
      <section className="relative max-w-5xl mx-auto px-4 pb-10 sm:pb-16 text-center">
        <div className="bg-board-cell rounded-xl p-6 sm:p-8 border border-board-border">
          <h2 className="font-display text-xl sm:text-2xl font-black text-vsw-200 uppercase tracking-wider mb-2">
            Climb the Ranks
          </h2>
          <p className="text-vsw-300 text-sm mb-5">
            Compete for the highest rating on the global leaderboard.
          </p>
          <button
            onClick={() => navigate("/leaderboard")}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg border border-vsw-400/40 text-vsw-200 hover:bg-vsw-400/10 transition-all text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            View Leaderboard
          </button>
        </div>
      </section>
    </div>
  );
}
